import { MainProduct, ImageType } from "@/types/productTypes";
import { useAdminStore } from "../../../../store/useAdminStore";
import { useState, useEffect } from "react";
import ImagePickerDialog from "@/Components/ImagePicker/ImagePickerDialog";
import {
   GenericEditor,
   CustomFieldConfig,
} from "@/Components/GenericEditor/GenericEditor";
import { Button } from "@/Components/ui/button";
import { mainProductConfig } from "@/configs/editorConfigs";

type MainProductEditorProps = {
   product: MainProduct;
};

export default function MainProductEditor({ product }: MainProductEditorProps) {
   const { selectedProductId, updateProduct, fetchFolders, fetchImages } =
      useAdminStore();
   const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
   const [selectedImage, setSelectedImage] = useState<ImageType | null>(
      product.image || null
   );
   const [allReadyFetched, setAllReadyFetched] = useState(false);

   const onProductChange = (field: any, value: any) => {
      if (!selectedProductId) return;
      updateProduct(selectedProductId, field, value);
   };

   useEffect(() => {
      if (isImagePickerOpen && !allReadyFetched) {
         fetchImagesAndFolders();
      }
   }, [isImagePickerOpen, allReadyFetched]);

   const fetchImagesAndFolders = async () => {
      try {
         await fetchImages();
         await fetchFolders();
         setAllReadyFetched(true);
      } catch (error) {
         console.error("Error fetching data:", error);
      }
   };

   const handleImageSelect = (image: ImageType) => {
      try {
         const recentIds: number[] = JSON.parse(
            localStorage.getItem("recentImages") || "[]"
         );
         const newRecentIds = [
            image.id,
            ...recentIds.filter((id) => id !== image.id),
         ].slice(0, 6);
         localStorage.setItem("recentImages", JSON.stringify(newRecentIds));

         setSelectedImage(image);
         onProductChange("imageId", image.id);
         setIsImagePickerOpen(false);
      } catch (error) {
         console.error("Error saving recent image:", error);
      }
   };

   const customFields: CustomFieldConfig<MainProduct>[] = [
      {
         key: "image",
         label: "Product Image",
         type: "custom",
         render: () => (
            <div className="flex items-center space-x-2">
               {selectedImage && (
                  <div className="relative w-24 h-24 border rounded overflow-hidden">
                     <img
                        src={selectedImage.url}
                        alt={selectedImage.title || "Product image"}
                        className="w-full h-full object-cover"
                     />
                  </div>
               )}
               <Button onClick={() => setIsImagePickerOpen(true)}>
                  {selectedImage ? "Change Image" : "Select Image"}
               </Button>
            </div>
         ),
      },
   ];

   return (
      <div className="flex flex-row w-full h-full">
         <GenericEditor
            item={product}
            config={mainProductConfig}
            customConfig={customFields}
            onChange={onProductChange}
            title="Edit Product"
         />
         <ImagePickerDialog
            open={isImagePickerOpen}
            onOpenChange={setIsImagePickerOpen}
            onImageSelect={handleImageSelect}
            dialogTitle="Select Product Image"
         />
      </div>
   );
}
